import {
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    Radio,
    RadioGroup,
    Typography,
} from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { sanitizeHtml } from "utils/sanitizeHtml";
import { Answer, AnswerAlert } from "../../../../models/Answer";
import AnswerDispute from "../../../../models/AnswerDispute";
import Question, { QuestionType } from "../../../../models/Question";
import { Tag, TagSentiment } from "../../../../models/Tag";
import AlertSvg from "../../../../SvgIcons/AlertSvg";
import ThumbsUpSvg from "../../../../SvgIcons/ThumbsUpSvg";
import theme from "../../../../Theme/AppTheme";
import { useStore } from "../../../../utils/useStore";
import AcxAnswerSelector from "../../../UI/AcxAnswerSelector";
import { ModuleUIModel } from "../../Models/ModuleUIModel";
import { EvalStore } from "../../Stores/EvalStore";
import AIExplanationCards from "./AIExplanations/AIExplanationsCards";
import SmartPredictToggle from "./AIExplanations/SmartPredictToggle";
import QuoteNote from "./QuoteNote";
import QuoteNoteButtonContainer from "./QuoteNoteButtonContainer";
import AcxQuestionDescription from "components/UI/AcxQuestionDescription";
import AcxQuestionRubric from "components/UI/AcxQuestionRubric";

interface OwnProps {
    question: Question;
    moduleUIModel?: ModuleUIModel;
    standalone?: boolean;
    leftBorderColor?: string;
    isDisabled?: boolean;
    showDisputeCheckbox?: boolean;
    answerDisputes?: AnswerDispute[];
}

type Props = OwnProps;

const ScaledResponseAnswer = observer((props: Props) => {
    const store = useStore(EvalStore);

    const answer = store.currentEval?.getAnswerForQuestion(
        props.question,
        props.moduleUIModel?.licensedModule.evaluationModuleId,
    );

    const activeTag =
        (answer?.activeTags.length ?? 0) > 0
            ? answer?.activeTags[0]
            : undefined;

    const isSmartPredicted = answer?.hasAccuratePredictions;

    const [showNote, setShowNote] = useState<boolean>(Boolean(answer?.hasNote));
    const [showQuote, setShowQuote] = useState<boolean>(
        Boolean(answer?.hasClips),
    );
    const [flagActive, setFlagActive] = useState<boolean>(
        answer?.answerAlert === AnswerAlert.PositiveAlert ||
            answer?.answerAlert === AnswerAlert.NegativeAlert,
    );

    const isPositiveSentimentAnswer = activeTag?.isAnswerPostiveSentiment;
    const isNegativeSentimentAnswer = activeTag?.isAnswerNegativeSentiment;

    const isNeedsAttn = answer?.isNeedsAttention;
    const isExemplary = answer?.isExemplary;

    function toggleNoteField() {
        setShowNote((prevState) => !prevState);
    }

    function toggleQuoteField() {
        setShowQuote((prevState) => !prevState);
    }

    const toggleAnswerAlert = () => {
        if (flagActive === true) {
            setFlagActive(false);
            if (activeTag && answer) answer.answerAlert = null;
        } else {
            if (activeTag && answer) {
                setFlagActive(true);
                if (
                    activeTag.sentiment === TagSentiment.Neutral ||
                    activeTag.sentiment === TagSentiment.Positive
                ) {
                    answer.answerAlert = AnswerAlert.PositiveAlert;
                } else {
                    answer.answerAlert = AnswerAlert.NegativeAlert;
                }
            }
        }
    };

    const bgColor = showNote || showQuote ? "unset" : "unset";

    useEffect(() => {
        setShowQuote(!!answer?.hasClips);
    }, [
        answer?.answerAlert,
        answer?.soundClipAnswers,
        answer?.hasClips,
        answer?.hasNote,
    ]);

    useEffect(() => {
        if (activeTag && answer && flagActive) {
            if (
                activeTag.sentiment === TagSentiment.Neutral ||
                activeTag.sentiment === TagSentiment.Positive
            ) {
                answer.answerAlert = AnswerAlert.PositiveAlert;
            } else {
                answer.answerAlert = AnswerAlert.NegativeAlert;
            }
        }
    }, [activeTag, answer, flagActive]);

    let showAIExplanationCard = store.showAIExplanation[props.question.id];

    useEffect(() => {
        props.moduleUIModel?.updateModuleScore(
            props.question,
            activeTag === undefined ? [] : [activeTag],
        );
    }, [props.question, props.moduleUIModel, activeTag, props.answerDisputes]);

    const hasValidDescription = !!sanitizeHtml(props.question.helperText ?? "");

    return (
        <Grid
            id={`question-${props.question.id}`}
            item
            xs={12}
            style={{
                backgroundColor: bgColor,
                border: "1px solid transparent",
                borderRadius: "4px",
                marginBottom: "24px",
                paddingLeft:
                    props.question.parentId || props.leftBorderColor
                        ? "16px"
                        : "0px",
                borderLeft: props.leftBorderColor
                    ? `2px solid ${props.leftBorderColor}`
                    : "none",
            }}
            container
            direction={"column"}
            justifyContent={"flex-start"}
            alignItems="stretch"
        >
            <Grid
                item
                xs={12}
                container
                direction={"row"}
                justifyContent={"flex-start"}
                alignItems={"center"}
                wrap={"nowrap"}
                sx={{
                    paddingX: !props.standalone ? 3 : 0,
                    marginBottom: props.standalone ? 0.25 : 1,
                    marginTop: props.standalone ? 0.5 : 2,
                }}
            >
                {Boolean(answer && props.showDisputeCheckbox) && (
                    <Checkbox
                        id={"DisputeAnswer-" + props.question.id}
                        title="Check to Dispute this Answer."
                        checked={answer?.isDisputed ? true : false}
                        onClick={answer?.toggleIsDisputed}
                    />
                )}

                <Grid
                    container
                    item
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={1}
                >
                    <Grid item>
                        <Typography
                            sx={{
                                color: theme.palette.text.primary,
                                fontSize: props.standalone ? "12px" : "14px",
                                letterSpacing: "0",
                                lineHeight: "20px",
                            }}
                            fontWeight={"bold"}
                        >
                            {props.question?.questionText}
                            {props.question.required && !answer?.isAnswered && (
                                <span
                                    style={{
                                        color: theme.palette.error.main,
                                    }}
                                >
                                    *
                                </span>
                            )}
                        </Typography>
                    </Grid>
                    <SmartPredictToggle
                        question={props.question}
                        moduleUIModel={props.moduleUIModel}
                    />
                </Grid>
            </Grid>

            {!!props.question.rubric && (
                <Grid
                    item
                    container
                    xs={12}
                    sx={{
                        paddingY: 1,
                        paddingX: !props.standalone ? 3 : 0,
                    }}
                >
                    <AcxQuestionRubric urlString={props.question.rubric} />
                </Grid>
            )}

            {hasValidDescription && (
                <Grid
                    item
                    container
                    xs={12}
                    sx={{
                        paddingX: !props.standalone ? theme.spacing(3) : 0,
                    }}
                >
                    <AcxQuestionDescription
                        text={props.question.helperText ?? ""}
                        isModalForTablesVariant={props.standalone}
                    />
                </Grid>
            )}
            {props.question.type !== QuestionType.QuestionHeading && (
                <Grid
                    item
                    xs={12}
                    direction={"row"}
                    wrap={"nowrap"}
                    container
                    justifyContent={"flex-start"}
                    alignItems={"center"}
                    sx={{
                        paddingX: !props.standalone ? 3 : 0,
                        width: "100%",
                        height: "100%",
                    }}
                >
                    <Grid item xs={10}>
                        <AnswerSelectorComponent
                            question={props.question}
                            store={store}
                            answer={answer}
                            activeTag={activeTag}
                            moduleUIModel={props.moduleUIModel}
                            leftBorderColor={props.leftBorderColor}
                            standalone={props.standalone}
                            isDisabled={props.isDisabled}
                        />
                    </Grid>
                    {!props.standalone && (
                        <Grid
                            item
                            xs={2}
                            container
                            justifyContent="space-evenly"
                            wrap={"nowrap"}
                        >
                            {props.moduleUIModel?.isAlertableAnswer(
                                props.question.id,
                            ) &&
                                (isPositiveSentimentAnswer ||
                                    isNegativeSentimentAnswer) && (
                                    <Grid
                                        item
                                        xs={12}
                                        lg={3}
                                        style={{
                                            position: "relative",
                                            top: -8,
                                        }}
                                    >
                                        {isPositiveSentimentAnswer ? (
                                            <IconButton
                                                onClick={
                                                    answer?.togglePositiveAlert
                                                }
                                                size="large"
                                            >
                                                <ThumbsUpSvg
                                                    color={
                                                        isExemplary
                                                            ? theme.palette
                                                                  .success.main
                                                            : undefined
                                                    }
                                                    opacity={
                                                        isExemplary
                                                            ? 1
                                                            : undefined
                                                    }
                                                />
                                            </IconButton>
                                        ) : isNegativeSentimentAnswer ? (
                                            <IconButton
                                                onClick={
                                                    answer?.toggleNegativeAlert
                                                }
                                                size="large"
                                            >
                                                <AlertSvg
                                                    color={
                                                        isNeedsAttn
                                                            ? theme.palette.red
                                                                  .main
                                                            : undefined
                                                    }
                                                    opacity={
                                                        isNeedsAttn
                                                            ? 1
                                                            : undefined
                                                    }
                                                />
                                            </IconButton>
                                        ) : null}
                                    </Grid>
                                )}
                            <QuoteNoteButtonContainer
                                interactionType={
                                    store.currentEval?.interaction
                                        ?.interactionType
                                }
                                toggleNote={toggleNoteField}
                                showNote={showNote}
                                toggleQuote={toggleQuoteField}
                                showQuote={showQuote}
                                toggleAnswerAlert={toggleAnswerAlert}
                                flagActive={flagActive}
                                isDisabled={props.isDisabled}
                                restrictMedia={
                                    !store.authStore.isUserUltra() &&
                                    store.authStore.canUserView(
                                        "Restrict Media",
                                    )
                                }
                            />
                        </Grid>
                    )}
                </Grid>
            )}
            {!props.standalone && (
                <QuoteNote
                    question={props.question}
                    answer={answer}
                    showNote={showNote}
                    showQuote={showQuote}
                    noteXs={12}
                    columnDirection={"column"}
                    isDisabled={props.isDisabled}
                    evaluationModuleId={
                        props.moduleUIModel?.licensedModule.evaluationModuleId
                    }
                />
            )}
            {isSmartPredicted && showAIExplanationCard && activeTag && (
                <AIExplanationCards
                    key={answer.questionId}
                    activeTags={[activeTag]}
                    predictedTags={answer.predictedTags}
                    questionId={props.question.id}
                    answer={answer}
                />
            )}
        </Grid>
    );
});

const AnswerSelectorComponent = observer(
    (
        props: OwnProps & {
            children?: React.ReactNode;
            store: EvalStore;
            answer: Answer | undefined;
            activeTag: Tag | undefined;
        },
    ) => {
        const messageStore = props.store.rootStore.getStore(MessageStore);
        const options =
            props.moduleUIModel?.getTagsFormattedForScaledResponse(
                props.question.id,
            ) ?? [];

        const evalModuleId =
            props.moduleUIModel?.licensedModule.evaluationModuleId;

        if (props.question.answerType.variation === "Checkbox") {
            function onCheckboxAnswerSelect(
                event: React.ChangeEvent<HTMLInputElement>,
            ) {
                const value = event.target.checked;
                const selectedTag = options.find(
                    (value1) =>
                        value1.data.value?.toLowerCase() ===
                        `${value}`.toLowerCase(),
                )?.data;

                if (selectedTag) {
                    props.store.currentEval?.setAnswerForQuestion(
                        props.question,
                        [selectedTag],
                        props.answer,
                        evalModuleId,
                        props.store.enableAutoBindClips,
                        messageStore,
                    );
                } else {
                    props.store.currentEval?.setAnswerForQuestion(
                        props.question,
                        [],
                        props.answer,
                        evalModuleId,
                        props.store.enableAutoBindClips,
                        messageStore,
                    );
                }
            }

            return (
                <>
                    <Checkbox
                        checked={
                            props.activeTag?.value?.toLocaleLowerCase() ===
                                "true" ||
                            props.activeTag?.value?.toLocaleLowerCase() ===
                                "yes"
                        }
                        disabled={props.isDisabled}
                        onChange={onCheckboxAnswerSelect}
                    />
                </>
            );
        } else if (props.question.answerType.variation === "Radio") {
            function onRadioAnswerSelect(
                event: React.ChangeEvent<HTMLInputElement>,
            ) {
                const value = event.target.value;
                const selectedTag = options.find(
                    (value1) =>
                        value1.data.value?.toLowerCase() ===
                        `${value}`.toLowerCase(),
                )?.data;

                if (selectedTag) {
                    props.store.currentEval?.setAnswerForQuestion(
                        props.question,
                        [selectedTag],
                        props.answer,
                        evalModuleId,
                        props.store.enableAutoBindClips,
                        messageStore,
                    );
                } else {
                    props.store.currentEval?.setAnswerForQuestion(
                        props.question,
                        [],
                        props.answer,
                        evalModuleId,
                        props.store.enableAutoBindClips,
                        messageStore,
                    );
                }
            }

            return (
                <RadioGroup
                    row
                    value={props.activeTag?.value ?? null}
                    onChange={onRadioAnswerSelect}
                >
                    {options.map((value) => {
                        return (
                            <FormControlLabel
                                key={value.data.id}
                                value={value.data.value}
                                control={<Radio color="secondary" />}
                                label={value.data.value}
                                labelPlacement="end"
                                disabled={props.isDisabled}
                            />
                        );
                    })}
                </RadioGroup>
            );
        } else {
            function onAnswerSelect(arg: Tag) {
                if (arg === undefined) {
                    props.store.currentEval?.setAnswerForQuestion(
                        props.question,
                        [],
                        props.answer,
                        evalModuleId,
                        props.store.enableAutoBindClips,
                        messageStore,
                    );
                } else {
                    props.store.currentEval?.setAnswerForQuestion(
                        props.question,
                        [arg],
                        props.answer,
                        evalModuleId,
                        props.store.enableAutoBindClips,
                        messageStore,
                    );
                }
            }

            return (
                <>
                    <AcxAnswerSelector
                        options={options}
                        onSelect={onAnswerSelect}
                        defaultValue={props.activeTag}
                        buttonRootStyles={{ minHeight: "34px" }}
                        isDisabled={props.isDisabled}
                    />
                </>
            );
        }
    },
);

export default ScaledResponseAnswer;
